import { SelectorReturnType } from '@vfde-sails/types';
import { selectTrackingPayload } from 'Container/App/selectors';
import {
    getCustomerFlow,
    getFlowType,
    getLoginData,
    getSiteStructure,
    normalizeTrackingPayloadName,
    PageViewTrackingData,
    PageViewTrackingDataWithError,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_PRODUCT_LINE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_SITE_AREA_MOBILITY,
    TrackingBtx,
    TrackingError,
    TrackingLoginStatus,
    TrackingPageType,
    TrackingProduct,
    TrackingProductType,
    TrackingProposition,
} from '@vfde-sails/tracking';
import {
    ADDITIONAL_PAGE_OPTIONS,
    BTX_BNT,
    FLOW_SUBSCRIPTIONS_FIRST,
    NSF_STORAGE_CURIOSITY_FLOW,
} from '@vfde-sails/constants';
import {
    getSessionStorageItemJson,
    NsfStorage,
} from '@vfde-sails/storage';
import {
    getFromArrayWithRunTimeStart,
    isTariffWithHardwareOffer,
} from '@vfde-sails/glados-v2';

/**
 * Create the page view tracking object
 */
export const createPageViewTrackingData = ({
    customerConfig,
    subscription,
    atomic: atomicDevice,
}: SelectorReturnType<typeof selectTrackingPayload>): PageViewTrackingData => {
    const { salesChannel } = customerConfig;
    const siteStructure = getSiteStructure(window[ADDITIONAL_PAGE_OPTIONS].pageName, BTX_BNT as any);
    const loginStatus = getLoginData() ? TrackingLoginStatus.LoggedIn : TrackingLoginStatus.NotLoggedIn;
    const lineOfCustomer = getCustomerFlow(salesChannel!);
    const curiosityFlow = getSessionStorageItemJson<NsfStorage>(NSF_STORAGE_CURIOSITY_FLOW)?.customer;
    const winbackFlag = curiosityFlow ? JSON.parse(curiosityFlow).isWinbackCustomer : false;

    let oOrder: PageViewTrackingData['oOrder'];

    if (subscription) {
        let aPropositionsName = subscription.tariffName;
        let productCategory = TrackingProductType.Tariff as TrackingProposition['productCategory'];

        const aProducts: TrackingProduct[] = [{
            sku: subscription.tariffId,
            name: subscription.tariffName,
            type: TrackingProductType.Tariff,
            ...subscription.priceConnectionFee && {
                priceOnce: `${subscription.priceConnectionFee.gross}`,
            },
            priceMonthly: `${getFromArrayWithRunTimeStart(subscription.priceMonthlyTariffWithoutDiscounts).gross}`,
            duration: 24,
            proposition: 1,
            units: 1,
        }];

        // Track device
        if (atomicDevice && isTariffWithHardwareOffer(subscription)) {
            aProducts.push({
                name: atomicDevice.atomicName,
                sku: atomicDevice.sku,
                type: TrackingProductType.Device,
                priceOnce: `${subscription.priceHardwareOneTime.gross}`,
                proposition: 1,
                units: 1,
            });
            aPropositionsName = `${atomicDevice.modelName} | ${subscription.tariffName}`;
            productCategory = TrackingProductType.Bundle;
        }

        // Track discounts
        for (const discount of subscription.monetaryDiscounts || []) {
            discount.gross < 0 && aProducts.push({
                sku: discount.discountId,
                name: normalizeTrackingPayloadName(discount.displayLabel),
                priceMonthly: discount.gross.toString(),
                priceOnce: '0',
                proposition: 1,
                type: TrackingProductType.Discount,
                units: 1,
                duration: 24,
            });
        }

        oOrder = {
            aProducts,
            aPropositions: [
                {
                    id: 1,
                    name: aPropositionsName,
                    targetAudience: lineOfCustomer,
                    retention: TrackingBtx.Bnt,
                    productType: TRACKING_PRODUCT_TYPE,
                    productLine: TRACKING_PRODUCT_LINE,
                    productCategory,
                    units: 1,
                },
            ],
        };
    }

    return {
        /* eslint-disable jsdoc/require-jsdoc, @typescript-eslint/naming-convention, camelcase */
        page_levels: siteStructure,
        login_status: loginStatus,
        line_of_customer: lineOfCustomer,
        is_winback_customer: winbackFlag,
        flow_type: getFlowType(customerConfig.flowType || FLOW_SUBSCRIPTIONS_FIRST, salesChannel || undefined),
        page_type: TrackingPageType.ProductDetail,
        site_area: TRACKING_SITE_AREA_MOBILITY,
        platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        business_transaction_type: TrackingBtx.Bnt,
        /* eslint-enable jsdoc/require-jsdoc, @typescript-eslint/naming-convention, camelcase */
        oOrder,
    };
};

/**
 * Create the page view tracking object with error
 */
export const createPageViewTrackingDataWithError = (
    trackingData: ReturnType<typeof selectTrackingPayload>,
    trackingError: TrackingError,
): PageViewTrackingDataWithError => {
    const pageViewTrackingData = createPageViewTrackingData(trackingData);

    return {
        ...pageViewTrackingData,
        ...trackingError,
    };
};
